import { lazy, Suspense, useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import type { UrlEntity } from '../domain/note'
import { linkPreviewImageUrl } from '../links/linkMetadata'
import { linkPreviewPresentation } from '../links/linkPreviewCategory'
import { linkTone } from './cardKind'
import { linkCardTitle, type CardKind, type CardRecipe } from '../search/cardRecipe'
import { HighlightedText } from './HighlightedText'
import { Icon } from './Icon'
import type { ContentType } from '../domain/note'

const MarkdownCardPreview = lazy(() => import('./MarkdownCardPreview').then((module) => ({ default: module.MarkdownCardPreview })))

/**
 * The Browse card's slots in their one order (search/cardRecipe.ts, design/studio/browse-card.css): media, then kind, title, line, body and
 * link rows. The footer stays SearchResultCard's shared row.
 */
export function BrowseCardMedia({ recipe, pictures, onImageFailed }: { recipe: CardRecipe; pictures: ReactNode; onImageFailed?: (source: string) => void }) {
  const media = recipe.media
  if (!media) return null
  if (media.type === 'pictures') return pictures ? <div className="browse-card__media" data-media="pictures">{pictures}</div> : null
  if (media.type === 'link') return <LinkMedia url={media.url} onFailed={onImageFailed} />
  const end = (part: typeof media.route.from) => (
    <span className="browse-card__end">
      <span className={part.code ? 'browse-card__code' : 'browse-card__code browse-card__code--place'} dir="auto">{part.code ?? part.place}</span>
      {part.code && part.place ? <span className="browse-card__city" dir="auto">{part.place}</span> : null}
    </span>
  )
  return (
    <div className="browse-card__media browse-card__route" data-media="route" data-tone={recipe.kind?.tone}>
      {end(media.route.from)}<span className="browse-card__way" aria-hidden="true"><Icon name={recipe.kind?.icon ?? 'flight'} /></span>{end(media.route.to)}
    </div>
  )
}

/** A link's picture as the card's media; a picture that fails is no media, never an empty box. */
function LinkMedia({ url, onFailed }: { url: UrlEntity; onFailed?: (source: string) => void }) {
  const source = linkPreviewImageUrl(url.imageUrl)
  const [failed, setFailed] = useState<string | null>(null)
  if (!source || failed === source) return null
  return (
    <div className="browse-card__media" data-media="link">
      <img src={source} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer"
        onError={() => { setFailed(source); if (url.imageUrl) onFailed?.(url.imageUrl) }} />
    </div>
  )
}

export function BrowseCardText({ recipe, terms, contentType, titlePending = false, interactive = true, previewOnly = false }: {
  recipe: CardRecipe
  terms: string[]
  contentType: ContentType
  titlePending?: boolean
  interactive?: boolean
  previewOnly?: boolean
}) {
  const kind = recipe.kind
  // Whether the card's height cuts the words: they then fade before the bottom inset (browse-card.css).
  const text = useRef<HTMLDivElement>(null)
  const [clipped, setClipped] = useState(false)
  useLayoutEffect(() => {
    const element = text.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const measure = () => setClipped(element.scrollHeight > element.clientHeight + 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    for (const child of element.children) observer.observe(child)
    return () => observer.disconnect()
  }, [recipe])
  return (
    <div className="browse-card__text" ref={text} data-clipped={clipped || undefined}>
      {kind ? <CardKindLine kind={kind} /> : null}
      {recipe.title ? (
        <h2 className={titlePending ? 'browse-card__title t-shimmer' : 'browse-card__title'} data-quote={recipe.quote || undefined} data-tone={recipe.quote ? kind?.tone : undefined}
          data-text={titlePending ? recipe.title : undefined} title={recipe.title} dir="auto">
          <HighlightedText text={recipe.title} terms={terms} />
        </h2>
      ) : null}
      {recipe.line ? <p className="browse-card__line" data-tone={kind?.tone} dir="auto">{recipe.line}</p> : null}
      {recipe.body?.type === 'markdown' ? (
        <Suspense fallback={<div className="search-result__preview-placeholder" aria-hidden="true" />}>
          <MarkdownCardPreview readOnly={previewOnly} markdown={recipe.body.markdown} terms={terms} />
        </Suspense>
      ) : recipe.body?.type === 'text' ? (
        <p className="browse-card__body" dir="auto"><HighlightedText text={recipe.body.text} terms={terms} /></p>
      ) : null}
      {recipe.links.length ? (
        <ul className="browse-card__links" aria-label="Links in this note" inert={!interactive || undefined}>
          {recipe.links.map(url => <LinkRow key={url.originalUrl} url={url} contentType={contentType} still={previewOnly} />)}
          {recipe.moreLinks > 0 ? <li className="browse-card__more">+{recipe.moreLinks} more</li> : null}
        </ul>
      ) : null}
    </div>
  )
}

/** What a card is, one way everywhere: Browse cards, an unpacked card and a link card in a note. A glyph in the kind's hue, its word, source, how soon. */
export function CardKindLine({ kind }: { kind: CardKind }) {
  return (
    <p className="browse-card__kind" data-tone={kind.tone}>
      <Icon name={kind.icon} />
      {kind.word ? <span className="browse-card__kind-word" dir="auto">{kind.word}</span> : null}
      {kind.source ? <span className="browse-card__kind-source" dir="auto">{kind.source}</span> : null}
      {kind.when ? <span className="browse-card__when" data-state={kind.when.state}>{kind.when.text}</span> : null}
    </p>
  )
}

/** A link that is not the card's subject: its kind's glyph, its name and its site, on one quiet row. Opens the page; the card opens the note. */
function LinkRow({ url, contentType, still = false }: { url: UrlEntity; contentType: ContentType; still?: boolean }) {
  const presentation = linkPreviewPresentation(url, contentType)
  const title = linkCardTitle(url, presentation.provider)
  const site = presentation.provider
  const stop = (event: MouseEvent) => event.stopPropagation()
  const content = (
    <>
      <Icon name={presentation.icon} />
      <span className="browse-card__link-text">
        <span className="browse-card__link-title" dir="auto">{title || site}</span>
        {title && title !== site ? <span className="browse-card__link-site" dir="auto">{site}</span> : null}
      </span>
    </>
  )
  // A still card (a preview of a note being chosen) shows the row without opening anything.
  return (
    <li className="browse-card__link" data-tone={linkTone(presentation.category)}>
      {still ? <span className="browse-card__link-row">{content}</span> : (
        <a className="browse-card__link-row" href={url.originalUrl} target="_blank" rel="noreferrer noopener" onClick={stop} onPointerDown={stop} aria-label={`${presentation.actionLabel}: ${title || site}`}>{content}</a>
      )}
    </li>
  )
}
